import React, { useState, useEffect } from "react";
import { X, Upload } from "lucide-react";
import { useDispatch } from "react-redux";
import { createCatalogModel } from "../../redux/actions/catalogActions";

const CatalogueModelForm = ({ onClose, selectedMainCatalogue, selectedModel }) => {

  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem("token");

  /* ---------------- MAIN CATALOGUES ---------------- */

  const [mainCatalogues, setMainCatalogues] = useState([]);

 useEffect(() => {

  const fetchMainCatalogues = async () => {

    try {

      const res = await fetch(
        `https://api.rmtechsolution.com/getMainCatalogues?merchantId=${token}`
      );

      const json = await res.json();

      if (json.success && Array.isArray(json.data)) {
        setMainCatalogues(json.data);
      }

    } catch (err) {
      console.error("Main catalogue fetch error", err);
    }

  };

  fetchMainCatalogues();

}, [token]);

  /* ---------------- IMAGE ---------------- */

  const [imageFile, setImageFile] = useState(null);

  const [form, setForm] = useState({
    merchantId: token || "",
    main_catalogue_id: "",
    name: "",
    slug: "",
    description: "",
    status: "active",
    image: null,
  });

  useEffect(() => {
    if (selectedMainCatalogue && !selectedModel) {
      setForm((prev) => ({
        ...prev,
        main_catalogue_id: selectedMainCatalogue.id,
      }));
    }
  }, [selectedMainCatalogue, selectedModel]);

  useEffect(() => {
    if (selectedModel) {
      setForm({
        merchantId: selectedModel.merchant_id || token || "",
        main_catalogue_id: selectedModel.main_catalogue_id || "",
        name: selectedModel.name || "",
        slug: selectedModel.slug || "",
        description: selectedModel.description || "",
        status: selectedModel.status || "active",
        image: selectedModel.image || null,
      });
      setImageFile(null);
    }
  }, [selectedModel, token]);

  /* ---------------- IMAGE SELECT ---------------- */

  const handleImageUpload = (file) => {

    if (!file) return;

    setImageFile(file);

    setForm((prev) => ({
      ...prev,
      image: URL.createObjectURL(file),
    }));

  };

  const removeImage = () => {

    setImageFile(null);

    setForm((prev) => ({
      ...prev,
      image: null,
    }));

  };

  /* ---------------- IMAGE UPLOAD ---------------- */

  const uploadImageBeforeCreate = async () => {

    if (!imageFile) return null;

    const fd = new FormData();
    fd.append("image", imageFile);
    fd.append("merchantId", Number(form.merchantId));

    const res = await fetch(
      "https://api.rmtechsolution.com/uploadCmsImage",
      {
        method: "POST",
        body: fd,
      }
    );

    const json = await res.json();

    if (!json.success || !json.imageUrl) {
      throw new Error("Image upload failed");
    }

    return json.imageUrl;

  };

  /* ---------------- SUBMIT ---------------- */

  const handleSubmit = async () => {

    if (!form.merchantId || !form.name || !form.slug) {
      alert("Merchant ID, catalogue name and slug are required");
      return;
    }

    setSaving(true);

    try {
      if (selectedModel) {
        let uploadedImageUrl = null;

        if (imageFile) {
          uploadedImageUrl = await uploadImageBeforeCreate();
        }

        const formData = new FormData();
        formData.append("merchant_id", form.merchantId);
        formData.append("id", selectedModel.id);
        formData.append("main_catalogue_id", form.main_catalogue_id);
        formData.append("name", form.name);
        formData.append("slug", form.slug);
        formData.append("description", form.description);
        formData.append("status", form.status);

        if (uploadedImageUrl) {
          formData.append("image", uploadedImageUrl);
        } else if (form.image) {
          formData.append("image", form.image);
        }

        const res = await fetch(
          "https://api.rmtechsolution.com/updateCatalogueModel.php",
          {
            method: "POST",
            body: formData,
          }
        );

        const json = await res.json();

        if (!json.success) {
          throw new Error(json.message || "Failed to update catalogue model");
        }

        alert("Catalogue model updated successfully");
        onClose();
      } else {
        let uploadedImageUrl = null;

        if (imageFile) {
          uploadedImageUrl = await uploadImageBeforeCreate();
        }

        await dispatch(
          createCatalogModel({
            ...form,
            image: uploadedImageUrl,
          })
        );

        alert("Catalogue model created successfully");
        onClose();
      }
    } catch (err) {
      console.error(err);
      alert(err.message || "Failed to save catalogue model");
    } finally {
      setSaving(false);
    }

  };

  /* ---------------- UI ---------------- */

  return (

    <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center p-4">

      <div className="bg-white w-full max-w-xl rounded-xl shadow-lg overflow-hidden max-h-[90vh] flex flex-col">

        {/* HEADER */}

        <div className="flex justify-between items-center px-6 py-4 border-b">

          <h2 className="text-xl font-bold">
            {selectedModel ? "Update Catalogue" : "Create Catalogue"}
          </h2>

          <button onClick={onClose}>
            <X size={20} />
          </button>

        </div>

        {/* BODY */}

        <div className="p-6 space-y-4 overflow-y-auto flex-1">

          {/* MERCHANT ID */}

          <div>

            <label className="block text-sm font-medium mb-1">
              Merchant ID *
            </label>

            <input
              value={form.merchantId}
              disabled
              className="w-full px-4 py-2 border rounded-lg bg-gray-100"
            />

          </div>

          {/* MAIN CATALOGUE */}

          <div>

            <label className="block text-sm font-medium mb-1">
              Main Catalogue
            </label>

            <select
              value={form.main_catalogue_id}
              onChange={(e) =>
                setForm({
                  ...form,
                  main_catalogue_id: e.target.value,
                })
              }
              className="w-full px-4 py-2 border rounded-lg"
            >

              <option value="">
                Select Main Catalogue
              </option>

              {mainCatalogues.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name}
                </option>
              ))}

            </select>

          </div>

          {/* CATALOGUE NAME */}

          <div>

            <label className="block text-sm font-medium mb-1">
              Catalogue Name *
            </label>

            <input
              value={form.name}
              onChange={(e) =>
                setForm({ ...form, name: e.target.value })
              }
              className="w-full px-4 py-2 border rounded-lg"
            />

          </div>

          {/* SLUG */}

          <div>

            <label className="block text-sm font-medium mb-1">
              Slug *
            </label>

            <input
              value={form.slug}
              onChange={(e) =>
                setForm({ ...form, slug: e.target.value })
              }
              className="w-full px-4 py-2 border rounded-lg"
            />

          </div>

          {/* DESCRIPTION */}

          <div>

            <label className="block text-sm font-medium mb-1">
              Description
            </label>

            <textarea
              rows={3}
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
              className="w-full px-4 py-2 border rounded-lg"
            />

          </div>

          {/* IMAGE */}

          <div>

            <label className="block text-sm font-medium mb-2">
              Catalogue Image
            </label>

            {!form.image ? (

              <div
                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer"
                onClick={() =>
                  document
                    .getElementById("catalogue-image-upload")
                    .click()
                }
              >

                <Upload className="mx-auto mb-2" />

                <p className="text-gray-500 text-sm">
                  Click to upload image
                </p>

                <input
                  id="catalogue-image-upload"
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) =>
                    handleImageUpload(e.target.files[0])
                  }
                />

              </div>

            ) : (

              <div className="relative w-40">

                <img
                  src={form.image}
                  alt="catalogue"
                  className="h-28 w-full object-cover rounded"
                />

                <button
                  onClick={removeImage}
                  className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1"
                >
                  <X size={12} />
                </button>

              </div>

            )}

          </div>

        </div>

        {/* FOOTER */}

        <div className="flex justify-end gap-3 px-6 py-4 border-t">

          <button
            onClick={onClose}
            className="px-5 py-2 border rounded-lg"
          >
            Cancel
          </button>

          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-6 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl flex items-center"
          >

            {saving ? "Saving..." : selectedModel ? "Update Catalogue" : "Create Catalogue"}

          </button>

        </div>

      </div>

    </div>

  );

};

export default CatalogueModelForm;